<template>
  <div class="mock-exam-page">
    <div class="mock-exam-page__content">

      <LoadingSpinner v-if="loading" message="در حال بارگذاری آزمون‌ها..." />

      <ErrorMessage
        v-else-if="errorMessage"
        title="بارگذاری آزمون‌ها انجام نشد"
        :message="errorMessage"
        @retry="loadBooks"
      />

      <EmptyState
        v-else-if="!currentSections.length"
        icon="bi-journal-x"
        title="آزمونی موجود نیست"
        message="هنوز کتاب کمبریجی برای نمایش وجود ندارد."
      />

      <template v-else>
        <div
          v-for="(section, sectionIndex) in currentSections"
          :key="section.title"
          class="mock-exam-page__section"
          :style="{ animationDelay: `${sectionIndex * 60}ms` }"
        >
          <div class="mock-exam-page__section-header">
            <span class="mock-exam-page__section-mark" />
            <h5 class="mock-exam-page__section-title">{{ section.title }}</h5>
          </div>

          <div class="row row-cols-1 row-cols-sm-2 row-cols-md-4 g-3 m-0">
            <div class="col" v-for="test in section.tests" :key="test.label">
              <router-link
                :to="{
                  name: 'mockExamPageDtail',
                  params: {
                    level: section.title,
                    testId: test.number,
                  },
                }"
                class="mock-exam-page__test-link"
              >
                <div class="mock-exam-page__test-card">
                  <span class="mock-exam-page__test-label">{{ test.label }}</span>
                  <span
                    class="mock-exam-page__status-badge"
                    :class="`mock-exam-page__status-badge--${test.status}`"
                  >
                    <i
                      v-if="test.status === 'completed'"
                      class="bi bi-check2 mock-exam-page__status-check"
                      aria-hidden="true"
                    />
                    <i
                      v-else-if="test.status === 'locked'"
                      class="bi bi-lock-fill"
                      aria-hidden="true"
                    />
                    <i
                      v-else-if="test.status === 'in_progress'"
                      class="bi bi-hourglass-split"
                      aria-hidden="true"
                    />
                    {{ statusLabel(test.status) }}
                  </span>
                </div>
              </router-link>
            </div>
          </div>
        </div>

        <nav class="mock-exam-page__pagination-wrap" aria-label="صفحه‌بندی کتاب‌ها">
          <div class="mock-exam-page__pagination">
            <button
              type="button"
              class="mock-exam-page__page-btn mock-exam-page__page-btn--nav"
              :disabled="currentPage === 1"
              aria-label="صفحه قبل"
              @click="changePage(currentPage - 1)"
            >
              <i class="bi bi-chevron-right" aria-hidden="true" />
            </button>

            <button
              v-for="page in totalPages"
              :key="page"
              type="button"
              class="mock-exam-page__page-btn"
              :class="{ 'mock-exam-page__page-btn--active': currentPage === page }"
              :aria-current="currentPage === page ? 'page' : undefined"
              @click="changePage(page)"
            >
              {{ page }}
            </button>

            <button
              type="button"
              class="mock-exam-page__page-btn mock-exam-page__page-btn--nav"
              :disabled="currentPage === totalPages"
              aria-label="صفحه بعد"
              @click="changePage(currentPage + 1)"
            >
              <i class="bi bi-chevron-left" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { getBooks, getBookTests } from "@/api/books";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import EmptyState from "@/components/common/EmptyState.vue";

const BOOKS_PER_PAGE = 5;

const STATUS_LABELS = {
  completed: "تکمیل‌شده",
  locked: "قفل",
  in_progress: "در حال انجام",
  not_started: "شروع نشده",
};

const currentPage = ref(1);
const loading = ref(true);
const errorMessage = ref("");
const sections = ref([]);

const codeNumber = (code) => Number(String(code).replace(/\D/g, "")) || 0;

function statusLabel(status) {
  return STATUS_LABELS[status] ?? STATUS_LABELS.not_started;
}

async function loadBooks() {
  loading.value = true;
  errorMessage.value = "";
  try {
    const { items } = await getBooks({ page: 1, size: 100 });

    const sorted = [...items].sort(
      (a, b) => codeNumber(b.code) - codeNumber(a.code)
    );

    const withTests = await Promise.all(
      sorted.map(async (book) => {
        let tests = [];
        try {
          tests = await getBookTests(book.id);
        } catch {
          tests = [];
        }
        return {
          title: book.code,
          tests: tests
            .sort((a, b) => a.number - b.number)
            .map((t) => ({
              number: t.number,
              label: `${book.code}Test${t.number}`,
              status: t.status ?? "completed",
            })),
        };
      })
    );

    sections.value = withTests;
    currentPage.value = 1;
  } catch (e) {
    errorMessage.value = handleError(e, "MockExamPage.loadBooks");
  } finally {
    loading.value = false;
  }
}

const totalPages = computed(() =>
  Math.max(1, Math.ceil(sections.value.length / BOOKS_PER_PAGE))
);

const currentSections = computed(() => {
  const start = (currentPage.value - 1) * BOOKS_PER_PAGE;
  return sections.value.slice(start, start + BOOKS_PER_PAGE);
});

function changePage(page) {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
}

onMounted(loadBooks);
</script>

<style scoped>
.mock-exam-page {
  width: 100%;
}

.mock-exam-page__content {
  background: var(--color-card, #ffffff);
  color: var(--color-text, #4C372A);
  padding: 28px 30px 32px;
  border: 1px solid var(--color-border, #ECE4D9);
  border-radius: 24px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(76, 55, 42, 0.06);
}

.mock-exam-page__section {
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: mock-exam-fade-in 0.45s ease forwards;
}

@keyframes mock-exam-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mock-exam-page__section-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.85rem;
}

.mock-exam-page__section-mark {
  width: 8px;
  height: 20px;
  border-radius: 999px;
  background: linear-gradient(180deg, #6D5EF8 0%, #865FEA 100%);
  margin-left: 0.5rem;
  flex-shrink: 0;
  box-shadow: 0 0 10px rgba(109, 94, 248, 0.25);
}

.mock-exam-page__section-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #4C372A;
  letter-spacing: 0.01em;
}

.mock-exam-page__test-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.mock-exam-page__test-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  height: 56px;
  padding: 0 1rem;
  background: #fff;
  border: 1px solid #ECE4D9;
  border-radius: 14px;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.mock-exam-page__test-link:hover .mock-exam-page__test-card {
  background: #f5f3ff;
  border-color: #6D5EF8;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(109, 94, 248, 0.12);
}

.mock-exam-page__test-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #4C372A;
}

.mock-exam-page__status-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
  border: none;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.mock-exam-page__status-badge .bi {
  font-size: 0.8rem;
  line-height: 1;
}

.mock-exam-page__status-badge--completed {
  background: #f0fdf4;
  color: #15803d;
}

.mock-exam-page__status-badge--completed .mock-exam-page__status-check {
  font-size: 0.85rem;
  font-weight: 700;
  color: #15803d;
}

.mock-exam-page__status-badge--locked {
  background: #f8fafc;
  color: #94a3b8;
}

.mock-exam-page__status-badge--in_progress {
  background: #eff6ff;
  color: #6D5EF8;
}

.mock-exam-page__status-badge--not_started {
  background: #FBF7FC;
  color: #8C8176;
}

.mock-exam-page__pagination-wrap {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.mock-exam-page__pagination {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid #ECE4D9;
  border-radius: 14px;
  background: #fff;
}

.mock-exam-page__page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #ECE4D9;
  border-radius: 10px;
  background: transparent;
  color: #8C8176;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    color 0.18s ease,
    background-color 0.18s ease;
}

.mock-exam-page__page-btn--nav .bi {
  font-size: 0.9rem;
}

.mock-exam-page__page-btn--active {
  color: #6D5EF8;
  border-color: #6D5EF8;
  font-weight: 600;
  background: #f5f3ff;
}

.mock-exam-page__page-btn:hover:not(:disabled):not(.mock-exam-page__page-btn--active) {
  color: #4C372A;
  border-color: #6D5EF8;
  background: #faf9ff;
}

.mock-exam-page__page-btn--active:hover {
  color: #6D5EF8;
  border-color: #6D5EF8;
}

.mock-exam-page__page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.mock-exam-page__page-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(109, 94, 248, 0.25);
}

@media (max-width: 575.98px) {
  .mock-exam-page__content {
    padding: 20px 16px 24px;
    border-radius: 18px;
  }

  .mock-exam-page__test-card {
    height: auto;
    min-height: 56px;
    flex-wrap: wrap;
    padding: 12px;
  }
}
</style>
